import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Table,
  Tag,
  Statistic,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Tabs,
  Space,
  Divider
} from 'antd';
import {
  PlusOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import tradingService from '../services/tradingService';

const { TabPane } = Tabs;
const { Option } = Select;

const Trading = () => {
  const [strategies, setStrategies] = useState([]);
  const [positions, setPositions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
    // Set up polling for real-time updates
    const interval = setInterval(loadData, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load strategies
      try {
        const strategiesRes = await tradingService.getStrategies();
        const strategiesData = strategiesRes?.data?.results || strategiesRes?.data || [];
        setStrategies(Array.isArray(strategiesData) ? strategiesData : []);
      } catch (error) {
        console.error('Failed to load strategies:', error);
        setStrategies([]);
      }

      // Load positions
      try {
        const positionsRes = await tradingService.getPositions();
        const positionsData = positionsRes?.data?.results || positionsRes?.data || [];
        setPositions(Array.isArray(positionsData) ? positionsData : []);
      } catch (error) {
        console.error('Failed to load positions:', error);
        setPositions([]);
      }

      // Load logs
      try {
        const logsRes = await tradingService.getLogs({ limit: 50 });
        const logsData = logsRes?.data?.results || logsRes?.data || [];
        setLogs(Array.isArray(logsData) ? logsData : []);
      } catch (error) {
        console.error('Failed to load logs:', error);
        setLogs([]);
      }

      // Load dashboard data
      try {
        const dashboardRes = await tradingService.getTradingDashboard();
        setDashboardData(dashboardRes?.data || {});
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
        setDashboardData({});
      }

    } catch (error) {
      console.error('General error loading trading data:', error);
      message.error('Failed to load trading data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStrategy = async (values) => {
    try {
      await tradingService.createStrategy(values);
      message.success('Strategy created successfully');
      setModalVisible(false);
      form.resetFields();
      loadData();
    } catch (error) {
      message.error('Failed to create strategy: ' + error.message);
    }
  };

  const handleStrategyAction = async (strategyId, action) => {
    try {
      switch (action) {
        case 'activate':
          await tradingService.activateStrategy(strategyId);
          break;
        case 'deactivate':
          await tradingService.deactivateStrategy(strategyId);
          break;
        case 'pause':
          await tradingService.pauseStrategy(strategyId);
          break;
        default:
          return;
      }
      
      message.success(`Strategy ${action}d successfully`);
      loadData();
    } catch (error) {
      message.error(`Failed to ${action} strategy: ` + error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'green';
      case 'PAUSED': return 'orange';
      case 'INACTIVE': return 'default';
      case 'ERROR': return 'red';
      default: return 'default';
    }
  };

  const getPositionStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'blue';
      case 'CLOSED': return 'green';
      case 'PARTIAL': return 'orange';
      default: return 'default';
    }
  };

  const strategyColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Stock Code',
      dataIndex: 'stock_code',
      key: 'stock_code',
    },
    {
      title: 'Broker',
      dataIndex: 'broker',
      key: 'broker',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'Buy/Sell Threshold',
      key: 'thresholds',
      render: (_, record) => (
        `${record.buy_threshold}% / ${record.sell_threshold}%`
      ),
    },
    {
      title: 'Max Iterations',
      dataIndex: 'max_iterations',
      key: 'max_iterations',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'INACTIVE' && (
            <Button
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleStrategyAction(record.id, 'activate')}
            >
              Activate
            </Button>
          )}
          {record.status === 'ACTIVE' && (
            <>
              <Button
                size="small"
                icon={<PauseCircleOutlined />}
                onClick={() => handleStrategyAction(record.id, 'pause')}
              >
                Pause
              </Button>
              <Button
                danger
                size="small"
                icon={<StopOutlined />}
                onClick={() => handleStrategyAction(record.id, 'deactivate')}
              >
                Stop
              </Button>
            </>
          )}
          {record.status === 'PAUSED' && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleStrategyAction(record.id, 'activate')}
              >
                Resume
              </Button>
              <Button
                danger
                size="small"
                icon={<StopOutlined />}
                onClick={() => handleStrategyAction(record.id, 'deactivate')}
              >
                Stop
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const positionColumns = [
    {
      title: 'Strategy',
      dataIndex: 'strategy_name',
      key: 'strategy_name',
    },
    {
      title: 'Stock Code',
      dataIndex: 'stock_code',
      key: 'stock_code',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getPositionStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'Entry Price',
      dataIndex: 'entry_price',
      key: 'entry_price',
      render: (price) => price ? `₩${price.toLocaleString()}` : '-',
    },
    {
      title: 'Current Price',
      dataIndex: 'current_price',
      key: 'current_price',
      render: (price) => price ? `₩${price.toLocaleString()}` : '-',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'P&L',
      dataIndex: 'unrealized_pnl',
      key: 'unrealized_pnl',
      render: (pnl) => (
        <span style={{ color: pnl >= 0 ? '#52c41a' : '#ff4d4f' }}>
          ₩{pnl?.toLocaleString() || 0}
        </span>
      ),
    },
    {
      title: 'Iteration',
      key: 'iteration',
      render: (_, record) => (
        `${record.current_iteration}/${record.max_iterations}`
      ),
    },
  ];

  const logColumns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => new Date(timestamp).toLocaleString(),
    },
    {
      title: 'Strategy',
      dataIndex: 'strategy_name',
      key: 'strategy_name',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => (
        <Tag color={action === 'BUY' ? 'blue' : 'orange'}>{action}</Tag>
      ),
    },
    {
      title: 'Stock Code',
      dataIndex: 'stock_code',
      key: 'stock_code',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => price ? `₩${price.toLocaleString()}` : '-',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'P&L',
      dataIndex: 'pnl',
      key: 'pnl',
      render: (pnl) => (
        <span style={{ color: pnl >= 0 ? '#52c41a' : '#ff4d4f' }}>
          ₩{pnl?.toLocaleString() || 0}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <h1>Automated Trading System</h1>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            Create New Strategy
          </Button>
        </Col>
      </Row>


      {/* Dashboard Stats */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Strategies"
              value={dashboardData.active_strategies || 0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Open Positions"
              value={dashboardData.open_positions || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total P&L"
              value={dashboardData.total_pnl || 0}
              precision={0}
              valueStyle={{ color: dashboardData.total_pnl >= 0 ? '#3f8600' : '#cf1322' }}
              prefix={dashboardData.total_pnl >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              suffix="₩"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={dashboardData.success_rate || 0}
              precision={1}
              valueStyle={{ color: '#3f8600' }}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      <Tabs defaultActiveKey="1">
        <TabPane tab="Strategies" key="1">
          <Card title="Trading Strategies">
            <Table
              columns={strategyColumns}
              dataSource={Array.isArray(strategies) ? strategies : []}
              rowKey={(record) => record.id || record.key || Math.random()}
              loading={loading}
              pagination={{ pageSize: 10 }}
              locale={{
                emptyText: loading ? 'Loading...' : 'No trading strategies found. Create your first strategy to get started.'
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Positions" key="2">
          <Card title="Trading Positions">
            <Table
              columns={positionColumns}
              dataSource={Array.isArray(positions) ? positions : []}
              rowKey={(record) => record.id || record.key || Math.random()}
              loading={loading}
              pagination={{ pageSize: 10 }}
              locale={{
                emptyText: loading ? 'Loading...' : 'No open positions. Activate a strategy to start trading.'
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Trading Logs" key="3">
          <Card title="Trading Logs">
            <Table
              columns={logColumns}
              dataSource={Array.isArray(logs) ? logs : []}
              rowKey={(record) => record.id || record.key || Math.random()}
              loading={loading}
              pagination={{ pageSize: 20 }}
              locale={{
                emptyText: loading ? 'Loading...' : 'No trading logs available. Trading activity will appear here.'
              }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Create Strategy Modal */}
      <Modal
        title="Create New Trading Strategy"
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateStrategy}
        >
          <Form.Item
            name="name"
            label="Strategy Name"
            rules={[{ required: true, message: 'Please enter strategy name' }]}
          >
            <Input placeholder="Enter strategy name" />
          </Form.Item>

          <Form.Item
            name="stock_code"
            label="Stock Code"
            rules={[{ required: true, message: 'Please enter stock code' }]}
          >
            <Input placeholder="e.g., 005930 (Samsung Electronics)" />
          </Form.Item>

          <Form.Item
            name="broker"
            label="Broker"
            rules={[{ required: true, message: 'Please select broker' }]}
            initialValue="KIS"
          >
            <Select>
              <Option value="KIS">Korean Investment Securities</Option>
              <Option value="KIWOOM">Kiwoom Securities</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="buy_threshold"
                label="Buy Threshold (%)"
                rules={[{ required: true, message: 'Please enter buy threshold' }]}
                initialValue={-3.0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.1}
                  max={0}
                  formatter={value => `${value}%`}
                  parser={value => value.replace('%', '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="sell_threshold"
                label="Sell Threshold (%)"
                rules={[{ required: true, message: 'Please enter sell threshold' }]}
                initialValue={3.0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.1}
                  min={0}
                  formatter={value => `${value}%`}
                  parser={value => value.replace('%', '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="max_iterations"
                label="Max Iterations"
                rules={[{ required: true, message: 'Please enter max iterations' }]}
                initialValue={5}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  max={20}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="position_size"
                label="Position Size (₩)"
                rules={[{ required: true, message: 'Please enter position size' }]}
                initialValue={100000}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={10000}
                  step={10000}
                  formatter={value => `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₩\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="is_active"
            label="Activate Immediately"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>

          <Divider />

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Strategy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Trading;
